import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const UnitConverter = () => {
  const { token } = useAuth();
  
  const [units, setUnits] = useState({ weight: [], volume: [], temperature: [] });
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Conversion state
  const [conversionType, setConversionType] = useState('volume');
  const [inputValue, setInputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Common conversions
  const commonConversions = [
    { from: 'cup', to: 'ml', label: 'Cups → ml', type: 'volume' },
    { from: 'tbsp', to: 'ml', label: 'Tbsp → ml', type: 'volume' },
    { from: 'tsp', to: 'ml', label: 'Tsp → ml', type: 'volume' },
    { from: 'l', to: 'cup', label: 'Liters → Cups', type: 'volume' },
    { from: 'cc', to: 'ml', label: 'CC → ml', type: 'volume' },
    { from: 'cl', to: 'ml', label: 'Centiliters → ml', type: 'volume' },
    { from: 'oz', to: 'g', label: 'Oz → Grams', type: 'weight' },
    { from: 'lb', to: 'kg', label: 'Lb → Kg', type: 'weight' },
    { from: 'g', to: 'oz', label: 'Grams → Oz', type: 'weight' },
    { from: 'f', to: 'c', label: '°F → °C', type: 'temperature' },
    { from: 'c', to: 'f', label: '°C → °F', type: 'temperature' },
  ];

  useEffect(() => {
    fetchUnits();
    fetchReference();
  }, []);

  useEffect(() => {
    // Set default units when type changes
    const typeUnits = units[conversionType];
    if (typeUnits && typeUnits.length >= 2) {
      setFromUnit(typeUnits[0].key);
      setToUnit(typeUnits[1].key);
    }
    setResult(null);
  }, [conversionType, units]);

  const fetchUnits = async () => {
    try {
      const response = await fetch('/api/convert/units', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUnits(data.data);
        // Set initial units
        if (data.data.volume.length >= 2) {
          setFromUnit(data.data.volume[0].key);
          setToUnit(data.data.volume[1].key);
        }
      }
    } catch (err) {
      console.error('Failed to fetch units:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReference = async () => {
    try {
      const response = await fetch('/api/convert/reference', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setReference(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch reference:', err);
    }
  };

  const handleConvert = async () => {
    if (!inputValue || isNaN(parseFloat(inputValue))) {
      setError('Please enter a valid number');
      return;
    }

    setError('');
    
    try {
      const response = await fetch('/api/convert', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: parseFloat(inputValue),
          from: fromUnit,
          to: toUnit
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Conversion failed');
      }

      setResult(data.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleQuickConversion = (conv) => {
    setConversionType(conv.type);
    setFromUnit(conv.from);
    setToUnit(conv.to);
    if (inputValue) {
      // Auto-convert if there's already a value
      setTimeout(() => handleConvertWithUnits(conv.from, conv.to), 100);
    }
  };

  const handleConvertWithUnits = async (from, to) => {
    if (!inputValue || isNaN(parseFloat(inputValue))) return;

    try {
      const response = await fetch('/api/convert', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: parseFloat(inputValue),
          from,
          to
        })
      });

      const data = await response.json();
      if (data.success) {
        setResult(data.data);
      }
    } catch (err) {
      console.error('Conversion error:', err);
    }
  };

  const handleSwapUnits = () => {
    const temp = fromUnit;
    setFromUnit(toUnit);
    setToUnit(temp);
    setResult(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConvert();
    }
  };

  const getUnitDisplay = (key) => {
    const allUnits = [...units.weight, ...units.volume, ...units.temperature];
    const unit = allUnits.find(u => u.key === key);
    return unit ? unit.name : key;
  };

  if (loading) {
    return <div style={styles.loading}>Loading converter...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🔄 Unit Converter</h1>
      <p style={styles.subtitle}>Convert between weight, volume, and temperature units</p>

      {/* Conversion Type Tabs */}
      <div style={styles.tabs}>
        {['volume', 'weight', 'temperature'].map(type => (
          <button
            key={type}
            onClick={() => setConversionType(type)}
            style={{
              ...styles.tab,
              ...(conversionType === type ? styles.tabActive : {})
            }}
          >
            {type === 'volume' && '🥛'}
            {type === 'weight' && '⚖️'}
            {type === 'temperature' && '🌡️'}
            {' '}{type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* Converter */}
      <div style={styles.converterCard}>
        <div style={styles.converterRow}>
          {/* Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>From</label>
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.numberInput}
              placeholder="Enter value"
            />
            <select
              value={fromUnit}
              onChange={(e) => { setFromUnit(e.target.value); setResult(null); }}
              style={styles.select}
            >
              {units[conversionType]?.map(unit => (
                <option key={unit.key} value={unit.key}>{unit.name}</option>
              ))}
            </select>
          </div>

          {/* Swap Button */}
          <button onClick={handleSwapUnits} style={styles.swapBtn} title="Swap units">
            ⇄
          </button>

          {/* Output */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>To</label>
            <div style={styles.resultDisplay}>
              {result ? result.output.value : '—'}
            </div>
            <select
              value={toUnit}
              onChange={(e) => { setToUnit(e.target.value); setResult(null); }}
              style={styles.select}
            >
              {units[conversionType]?.map(unit => (
                <option key={unit.key} value={unit.key}>{unit.name}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button onClick={handleConvert} style={styles.convertBtn}>
          Convert
        </button>

        {result && (
          <div style={styles.resultText}>
            {result.formatted}
          </div>
        )}
      </div>

      {/* Quick Conversions */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>⚡ Quick Conversions</h2>
        <div style={styles.quickGrid}>
          {commonConversions.map((conv, idx) => (
            <button
              key={idx}
              onClick={() => handleQuickConversion(conv)}
              style={{
                ...styles.quickBtn,
                ...(fromUnit === conv.from && toUnit === conv.to ? styles.quickBtnActive : {})
              }}
            >
              {conv.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reference Tables */}
      {reference && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📋 Quick Reference</h2>
          
          <div style={styles.refGrid}>
            {/* Volume Reference */}
            <div style={styles.refCard}>
              <h3 style={styles.refTitle}>🥛 Volume</h3>
              <table style={styles.refTable}>
                <tbody>
                  {reference.volume.map((item, idx) => (
                    <tr key={idx}>
                      <td style={styles.refDesc}>{item.description}</td>
                      <td style={styles.refValue}>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Weight Reference */}
            <div style={styles.refCard}>
              <h3 style={styles.refTitle}>⚖️ Weight</h3>
              <table style={styles.refTable}>
                <tbody>
                  {reference.weight.map((item, idx) => (
                    <tr key={idx}>
                      <td style={styles.refDesc}>{item.description}</td>
                      <td style={styles.refValue}>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Temperature Reference */}
            <div style={styles.refCard}>
              <h3 style={styles.refTitle}>🌡️ Temperature</h3>
              <table style={styles.refTable}>
                <tbody>
                  {reference.temperature.map((item, idx) => (
                    <tr key={idx}>
                      <td style={styles.refDesc}>{item.description}</td>
                      <td style={styles.refValue}>{item.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '1rem 2rem'
  },
  title: {
    fontSize: '1.8rem',
    color: '#1e3a5f',
    marginBottom: '0.25rem'
  },
  subtitle: {
    color: '#6b7280',
    marginBottom: '1.5rem'
  },
  loading: {
    textAlign: 'center',
    padding: '2rem',
    color: '#6b7280'
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem'
  },
  tab: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#6b7280',
    transition: 'all 0.2s'
  },
  tabActive: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
    color: '#2563eb'
  },
  converterCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '2rem'
  },
  converterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap'
  },
  inputGroup: {
    flex: 1,
    minWidth: '150px'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem'
  },
  numberInput: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1.25rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    fontSize: '0.9rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  swapBtn: {
    padding: '0.75rem 1rem',
    fontSize: '1.5rem',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '0.5rem'
  },
  resultDisplay: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1.25rem',
    backgroundColor: '#f0fdf4',
    border: '2px solid #22c55e',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    textAlign: 'center',
    fontWeight: '600',
    color: '#166534',
    boxSizing: 'border-box',
    minHeight: '52px'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center'
  },
  convertBtn: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  resultText: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: '500',
    color: '#1e3a5f'
  },
  section: {
    marginBottom: '2rem'
  },
  sectionTitle: {
    fontSize: '1.25rem',
    color: '#1e3a5f',
    marginBottom: '1rem'
  },
  quickGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '0.5rem'
  },
  quickBtn: {
    padding: '0.6rem 0.75rem',
    fontSize: '0.85rem',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  quickBtnActive: {
    backgroundColor: '#dbeafe',
    borderColor: '#2563eb',
    color: '#1e40af'
  },
  refGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1rem'
  },
  refCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
  },
  refTitle: {
    fontSize: '1rem',
    color: '#1e3a5f',
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #e5e7eb'
  },
  refTable: {
    width: '100%',
    fontSize: '0.875rem'
  },
  refDesc: {
    padding: '0.4rem 0',
    color: '#374151'
  },
  refValue: {
    padding: '0.4rem 0',
    textAlign: 'right',
    color: '#6b7280',
    fontFamily: 'monospace'
  }
};

export default UnitConverter;
